import { useState, type ReactNode } from 'react';
import { Box, Chip, Collapse, IconButton, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { nombreTemporada } from '@film/shared';
import type { Descarga } from '../lib/api.js';
import { describirEstado } from '../lib/estados.js';
import { formatearTamano, formatearVelocidad } from '../lib/eventos.js';

interface Props {
  descargas: Descarga[];
  /** Cómo se pinta cada episodio al desplegar: la misma fila que una descarga suelta. */
  fila: (d: Descarga) => ReactNode;
}

/** Los episodios de una misma serie y temporada, en una sola tarjeta (D-27).
 *
 *  Veintidós filas iguales con el mismo título no dicen nada: lo que se quiere saber de una
 *  temporada es cuánto lleva en conjunto y cuántos episodios van en cada punto. Cada
 *  episodio sigue teniendo su fila —con sus botones, su detalle y sus avisos— al desplegar.
 *  Se agrupa por serie y temporada, no por lote: un episodio pedido suelto después de la
 *  temporada es de la misma temporada, y verlo aparte sería el mismo ruido. */
export function GrupoTemporada({ descargas, fila }: Props) {
  const [abierto, setAbierto] = useState(false);
  const primera = descargas[0]!;

  const total = descargas.reduce((a, d) => a + (d.sizeBytes ?? 0), 0);
  const hecho = descargas.reduce((a, d) => a + (d.sizeDone ?? 0), 0);
  const velocidad = descargas.reduce((a, d) => a + (d.speedBps ?? 0), 0);
  const pct = total > 0 ? Math.min(100, (hecho / total) * 100) : 0;

  // Cuántos episodios en cada punto, con las mismas palabras que cada fila.
  const porEstado = new Map<string, { n: number; color: ReturnType<typeof describirEstado>['color'] }>();
  for (const d of descargas) {
    const e = describirEstado(d);
    const v = porEstado.get(e.texto);
    if (v) v.n++;
    else porEstado.set(e.texto, { n: 1, color: e.color });
  }

  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ alignItems: { sm: 'center' } }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body1" sx={{ fontWeight: 600 }} noWrap>
            {primera.movieTitle ?? primera.fileName}
            {primera.movieYear && <Typography component="span" color="text.secondary"> ({primera.movieYear})</Typography>}
            <Typography component="span" color="text.secondary"> · {nombreTemporada(primera.season)}</Typography>
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
            {descargas.length} {descargas.length === 1 ? 'episodio' : 'episodios'}
            {primera.quality ? ` · ${primera.quality}` : ''}
          </Typography>
        </Box>
        <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
          {[...porEstado.entries()].map(([texto, { n, color }]) => (
            <Chip key={texto} size="small" color={color} variant="outlined" label={`${n} ${texto.toLowerCase()}`} />
          ))}
          <IconButton
            size="small"
            onClick={() => setAbierto((v) => !v)}
            aria-expanded={abierto}
            aria-label={abierto ? 'Ocultar episodios' : 'Ver episodios'}
            sx={{ transform: abierto ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}
          >
            <ExpandMoreIcon />
          </IconButton>
        </Stack>
      </Stack>

      <Box sx={{ mt: 1.5 }}>
        <LinearProgress variant="determinate" value={pct} sx={{ height: 6, borderRadius: 1 }} />
        <Stack direction="row" sx={{ justifyContent: 'space-between', mt: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            {pct.toFixed(1)}% · {formatearTamano(hecho)} de {formatearTamano(total)}
          </Typography>
          {velocidad > 0 && (
            <Typography variant="caption" color="text.secondary">{formatearVelocidad(velocidad)}</Typography>
          )}
        </Stack>
      </Box>

      <Collapse in={abierto} unmountOnExit>
        <Stack spacing={1.5} sx={{ mt: 2 }}>
          {descargas.map((d) => <Box key={d.id}>{fila(d)}</Box>)}
        </Stack>
      </Collapse>
    </Paper>
  );
}
